import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const EmployeeCertPublicView = () => {
  const { token } = useParams();
  const apiUrl = process.env.REACT_APP_API_URL;

  const [employee, setEmployee] = useState(null);
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(
          `${apiUrl}/employee_public_certs.php?token=${encodeURIComponent(token)}`
        );
        if (res.data && res.data.success) {
          setEmployee(res.data.employee);
          setCerts(res.data.certs || []);
        } else {
          setError(res.data.error || "Unable to load certifications.");
        }
      } catch (err) {
        setError("Error loading data.");
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchData();
    }
  }, [token, apiUrl]);

  if (loading) {
    return (
      <div className="text-center p-10 text-gray-500">
        Loading technician certifications...
      </div>
    );
  }

  if (error || !employee) {
    return (
      <div className="text-center p-10 text-red-500">
        {error || "Invalid or expired link."}
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 bg-white max-w-3xl mx-auto text-sm print:text-black print:bg-white">
      {/* Header */}
      <div className="text-center mb-6">
        {/* Optional: add logo here if you want */}
        {/* <img src={serviceLogo} alt="logo" className="mx-auto mb-4 w-32" /> */}
        <h1 className="text-2xl font-bold">Technician Certifications</h1>
        <div className="text-gray-500 mt-1">
          {employee.first_name} {employee.last_name}
        </div>
        <div className="text-gray-400 text-xs mt-1">
          {employee.title} · {employee.department}
        </div>
      </div>

      {/* Info text */}
      <div className="mb-4 text-gray-600 text-xs text-center">
        This page shows all certifications on file for this technician.
        Click <span className="font-semibold">Download</span> to retrieve a copy.
      </div>

      {/* Certs table */}
      <table className="w-full border mt-4 mb-6 text-xs md:text-sm">
        <thead>
          <tr className="bg-gray-100">
            <th className="border px-2 py-1 text-center">#</th>
            <th className="border px-2 py-1 text-left">Certification</th>
            <th className="border px-2 py-1 text-center">Issued</th>
            <th className="border px-2 py-1 text-center">Expires</th>
            <th className="border px-2 py-1 text-center">Action</th>
          </tr>
        </thead>
        <tbody>
          {certs.length === 0 ? (
            <tr>
              <td colSpan={5} className="text-center p-4 text-gray-400">
                No certifications found for this technician.
              </td>
            </tr>
          ) : (
            certs.map((cert, idx) => {
              const hasFile = cert.file_path && cert.file_path !== "";
              const downloadUrl = hasFile
                ? `${apiUrl}/download_employee_cert_file.php?token=${encodeURIComponent(
                    token
                  )}&cert_id=${cert.id}`
                : null;

              return (
                <tr key={cert.id}>
                  <td className="border px-2 py-1 text-center align-middle">
                    {idx + 1}
                  </td>
                  <td className="border px-2 py-1 align-middle">
                    <div className="font-medium">{cert.certification}</div>
                    {cert.note && (
                      <div className="text-[10px] text-gray-400 mt-1 whitespace-pre-line">
                        {cert.note}
                      </div>
                    )}
                  </td>
                  <td className="border px-2 py-1 text-center align-middle">
                    {cert.issued || "-"}
                  </td>
                  <td className="border px-2 py-1 text-center align-middle">
                    {cert.expires || "-"}
                  </td>
                  <td className="border px-2 py-1 text-center align-middle">
                    {hasFile ? (
                      <a
                        href={downloadUrl}
                        className="inline-block px-3 py-1 text-xs font-semibold rounded bg-indigo-600 text-white hover:bg-indigo-700"
                      >
                        Download
                      </a>
                    ) : (
                      <span className="text-[11px] text-gray-400">
                        No file on record
                      </span>
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      <div className="mt-4 text-[10px] text-gray-400 text-center">
        Generated by Ascend Software Collection · For verification purposes only.
      </div>

      <style>{`
        @media print {
          button, .noprint, a[href]:after { display: none !important; }
          body { background: white !important; }
        }
      `}</style>
    </div>
  );
};

export default EmployeeCertPublicView;
